import errorMessages from 'const/errorMessages'
import sessionKeys from 'const/sessionKeys'
import {
  OptionType,
  SelectedType
} from 'syf-component-library/ui/atoms/Select/typings'
import { FormErrors, FormState } from 'typings'
import { MultiSelectOption } from 'ui/atoms/MultiSelect/typings'
import { showEmployeeQuestion } from './relationshipQuestions'

export const testEmailFormat = (value: string): boolean =>
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value
  )
export const stringIsNotNumeric = (value: string): boolean =>
  !/^\d+$/.test(value)

export const checkMinLength = (value: string, desiredLength: number): boolean =>
  value.length < desiredLength

export const validateZip = (value: string, state?: string): boolean => {
  let validZip = false
  const zipInput = value
  const zipFirst3Digits = Number(zipInput.toString().slice(0, 3))
  if (state === 'AP' && zipFirst3Digits >= 962 && zipFirst3Digits <= 966) {
    validZip = true
  }
  if (state === 'CA' && zipFirst3Digits >= 900 && zipFirst3Digits <= 966) {
    validZip = true
  }
  if (state === 'WA' && zipFirst3Digits >= 980 && zipFirst3Digits <= 994) {
    validZip = true
  }
  return validZip
}

export const checkOptionType = (
  value: OptionType,
  unsureRelationship: boolean
): string => {
  if (!value.value && !unsureRelationship) {
    return errorMessages.required
  }
  return ''
}

export const checkValidCharacters = (
  value: string,
  extended = false
): boolean => {
  return !(
    !extended ? /^[a-zA-Z0-9\-\s]*$/ : /^[a-zA-Z0-9.\-',&/#:_\s]*$/
  ).test(value)
}

export const validateString = (
  value: string,
  extended = false,
  noMinimum = false
): string => {
  if (!noMinimum && checkMinLength(value, 1)) {
    return errorMessages.required
  }
  if (checkValidCharacters(value, extended)) {
    return extended
      ? errorMessages.invalidCharactersExtra
      : errorMessages.invalidCharacters
  }
  return ''
}

export const validateCardNumber = (value: string): string => {
  if (value && stringIsNotNumeric(value)) {
    return errorMessages.digitsOnly
  }
  return ''
}

/**
 * Evaluates if string is in valid format mm/dd/yyy
 * @param value string in date format
 * @returns boolean
 */
export const isValidDateString = (value: string): boolean => {
  return /^(0[1-9]|1[012])[/](0[1-9]|1[0-9]|2[0-9]|3[01])[/](0?[12]1?\d{3})$/.test(
    value
  )
}

/**
 * Converts string to Date in forma mm/dd/yyy
 * @param value string to convert
 * @returns Date
 */
export const convertStringToDate = (value: string): Date => {
  const splitDate = value.split('/')
  return new Date(
    Number(splitDate[2]),
    Number(splitDate[0]) - 1,
    Number(splitDate[1])
  )
}

const isValidBirthDate = (value: Date): boolean => {
  const now = new Date()
  return (
    new Date(value.getFullYear(), value.getMonth(), value.getDate()) <
    new Date(now.getFullYear(), now.getMonth(), now.getDate())
  )
}

let ParameterValues:
  | string
  | boolean
  | number
  | []
  | MultiSelectOption[]
  | string[]
  | Date
  | FormErrors
  | SelectedType
  | OptionType
  | undefined
  | null

export const validationRules = (
  rule: string,
  value: typeof ParameterValues,
  state?: FormState
): string => {
  const rules = {
    firstName: () => {
      return validateString(value as unknown as string)
    },
    lastName: () => {
      return validateString(value as unknown as string)
    },
    fromStateWhitelist: () => {
      if (!value) {
        // value.value
        return errorMessages.required
      }
      return ''
    },
    phone: () => {
      if (state?.hardOfHearing && !value) return ''
      if (
        !state?.hardOfHearing &&
        checkMinLength(value as unknown as string, 1)
      ) {
        return errorMessages.required
      }
      if (value && checkMinLength(value as unknown as string, 10)) {
        return errorMessages.invalidPhoneNumber
      }
      if (value && stringIsNotNumeric(value as unknown as string)) {
        return errorMessages.digitsOnly
      }
      return ''
    },
    phoneType: () => {
      if (state?.hardOfHearing && !state.phone) {
        return ''
      }
      if (state?.phone && !(value as OptionType).value) {
        return errorMessages.required
      }
      return ''
    },
    address: () => {
      return validateString(value as unknown as string)
    },
    secondaryAddress: () => {
      return validateString(value as unknown as string, false, true)
    },
    city: () => {
      return validateString(value as unknown as string)
    },
    state: () => {
      if (!(value as OptionType).value) {
        return errorMessages.required
      }
      return ''
    },
    zipCode: () => {
      if (checkMinLength(value as unknown as string, 1)) {
        return errorMessages.required
      }
      if (stringIsNotNumeric(value as unknown as string)) {
        return errorMessages.digitsOnly
      }
      if (checkMinLength(value as unknown as string, 5)) {
        return errorMessages.minFiveDigits
      }
      if (!validateZip(value as unknown as string, state?.state.value)) {
        return errorMessages.invalidZip
      }
      return ''
    },
    dateOfBirth: () => {
      if (!value || !(value instanceof Date)) {
        return errorMessages.required
      }
      if (!isValidBirthDate(value)) {
        return errorMessages.invalidBirthDate
      }
      return ''
    },
    creditCard: () => {
      return validateCardNumber(value as unknown as string)
    },
    savingProduct: () => {
      return validateCardNumber(value as unknown as string)
    },
    otherAccountType: () => {
      return validateCardNumber(value as unknown as string)
    },
    emailAddress: () => {
      if (!value && state?.confirmEmail) {
        return errorMessages.invalidEmail
      }
      if (!value) {
        return errorMessages.required
      }
      if (value && !testEmailFormat(value as unknown as string)) {
        return errorMessages.invalidEmail
      }
      return ''
    },
    confirmEmail: () => {
      if (
        state?.emailAddress &&
        checkMinLength(value as unknown as string, 1)
      ) {
        return errorMessages.required
      }
      if (!value) {
        return errorMessages.required
      }
      if (
        state?.emailAddress &&
        state?.emailAddress?.toLowerCase() !== (value as string).toLowerCase()
      ) {
        return errorMessages.invalidConfirmEmail
      }
      if (value && !testEmailFormat(value as unknown as string)) {
        return errorMessages.invalidEmail
      }
      return ''
    },
    additionalNotes: () => {
      return validateString(value as unknown as string, true, true)
    },
    recaptchaToken: () => {
      const captchaToken = sessionStorage.getItem(sessionKeys.recaptchaToken)
      if (!captchaToken) {
        return errorMessages.recaptchaError
      }
      return ''
    },
    rectification: () => {
      if (
        state?.rightToRectification &&
        (value as unknown as string).length <= 0
      ) {
        return errorMessages.required
      }
      return ''
    },
    relationshipSynchrony: () => {
      return checkOptionType(value as OptionType, !!state?.unsureRelationship)
    },
    installmentAccount: () => {
      if (
        state?.installmentAccountChecked &&
        value &&
        stringIsNotNumeric(value as unknown as string)
      ) {
        return errorMessages.digitsOnly
      }
      return ''
    },
    primaryAccountPayable: () => {
      return ''
    },
    businessCommercialAccount: () => {
      if (state?.primaryAccountPayable.value === 'Yes') {
        if (!value) {
          return errorMessages.required
        }
        if (stringIsNotNumeric(value as unknown as string)) {
          return errorMessages.digitsOnly
        }
      }
      return ''
    },
    unsureRelationship: () => {
      if (
        state?.relationshipSynchrony.value === 'Yes' &&
        !state?.installmentAccountChecked &&
        !state?.creditCardChecked &&
        !state?.savingAccountChecked &&
        !state?.businessCommercialAccountChecked &&
        !state?.unsureRelationship
      ) {
        return errorMessages.required
      }
      return ''
    },
    employee: () => {
      if (showEmployeeQuestion(state?.state?.value)) {
        return checkOptionType(value as OptionType, !!state?.unsureRelationship)
      }
      return ''
    }
  }
  const ruleToCheck: () => string = rules[rule as keyof typeof rules]
  return ruleToCheck && ruleToCheck()
}

const fieldValidations = (
  target: string,
  value: typeof ParameterValues,
  state: FormState
): string => validationRules(target, value, state)

export default fieldValidations
